import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { LinearProgress } from '@mui/material';
import clsx from 'clsx';

export default function DataTable({
    data,
    columns,
    hasNestedRows,
    renderNestedRow,
    customRenderCell,
    className,
    tdClassName,
    trClassName,
    loading,
    tbodyClassName,
}) {
    const [openRows, setOpenRows] = useState({});
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const toggleRow = id => {
        setOpenRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const toggleSort = key => {
        if (sortKey === key) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    const renderCell = (col, row, index) => {
        const value = row[col.key];

        if (customRenderCell) {
            const custom = customRenderCell(col, row, index);
            if (custom !== undefined && custom !== null) return custom;
        }

        if (col.iconKey && row[col.iconKey]) {
            return (
                <span className="inline-flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                        <img src={row[col.iconKey]} alt="icon" className="w-3 h-3" />
                    </span>
                    <span>{value}</span>
                </span>
            );
        }

        return value;
    };

    const cleanNumber = val => {
        if (typeof val === 'number') return val;
        if (typeof val !== 'string') return NaN;
        return parseFloat(val.replace(/,/g, '').trim());
    };

    const tryParseDate = val => {
        const parsed = Date.parse(val);
        return isNaN(parsed) ? null : new Date(parsed);
    };

    const sortedData = [...data].sort((a, b) => {
        if (!sortKey) return 0;

        const valA = a[sortKey];
        const valB = b[sortKey];

        if (valA === undefined || valB === undefined) return 0;

        const dateA = tryParseDate(valA);
        const dateB = tryParseDate(valB);
        if (dateA && dateB) {
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }

        const numA = cleanNumber(valA);
        const numB = cleanNumber(valB);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);

        if (isNumA && isNumB) {
            return sortDirection === 'asc' ? numA - numB : numB - numA;
        }

        const strA = String(valA);
        const strB = String(valB);
        return sortDirection === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });

    return (
        <div
            className={clsx(
                'overflow-x-auto rounded-[8px] rounded-t-none  bg-container-bg overflow-y-hidden relative border !border-section-br',
                className,
            )}>
            {loading && !data.length ? <LinearProgress /> : null}
            <table className="min-w-full text-sm text-left">
                <thead className="pr-[24px] pl-[24px] pt-[8px] pb-[8px]">
                    <tr className="bg-table-head-bg !border-0 !border-b !border-b-[#D1D1D6] dark:!border-b-[#3F3F46]">
                        {columns.map(col => (
                            <th
                                key={col.key}
                                className="px-4 py-2 whitespace-nowrap text-xs font-medium cursor-pointer w-[152px]"
                                onClick={() => toggleSort(col.key)}>
                                <div className="inline-flex items-center gap-[8px]">
                                    <span className="text-table-col-title text-[13px] font-600 leading-[20px] uppercase">
                                        {col.label}
                                    </span>
                                    {!col.noSort ? (
                                        <img
                                            src={`${imageBaseUrl}/junoclient/arrow-icon-sorting-light-theme.svg`}
                                            alt="Sort Icon"
                                            className="w-[8px]"
                                        />
                                    ) : null}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody
                    className={clsx(
                        `divide-y divide-[#E4E4E7] dark:divide-[#3F3F46]`,
                        tbodyClassName,
                    )}>
                    {sortedData.map((row, idx) => (
                        <>
                            <tr
                                key={row.id || idx}
                                data-e2e-row={`${row?.transactionId ? 'transaction-' : ''}${idx}`}
                                data-e2e={row.transactionId}
                                className={clsx(
                                    `hover:bg-gray-50 !bg-modal-bg cursor-pointer`,
                                    trClassName,
                                )}
                                onClick={() => renderNestedRow && toggleRow(row.id || idx)}>
                                {columns.map((col, index) => (
                                    <td
                                        key={col.key}
                                        className={clsx(
                                            `px-4  h-[64px] whitespace-nowrap uppercase text-[14px] !text-text font-500 z-2 leading-[20px]  `,
                                            tdClassName,
                                        )}>
                                        {renderCell(col, row, index)}
                                    </td>
                                ))}
                            </tr>
                            {hasNestedRows && renderNestedRow && openRows[row.id || idx] && (
                                <tr className="bg-gradient-to-b from-modal-bg to-[#f4f4f5] dark:to-[#18181B]">
                                    <td colSpan={100}>{renderNestedRow(row)}</td>
                                </tr>
                            )}
                        </>
                    ))}
                </tbody>
            </table>
            {!loading && !data.length ? (
                <span className="text-text uppercase block text-center p-4" data-e2e="no data found" data-e2e-msg="message"> No data found</span>
            ) : null}
            {loading && data.length ? (
                <div className="bg-text absolute top-0 w-full h-full opacity-25 z-3 flex justify-center">
                    <CircularProgress className="m-auto" size={50} />
                </div>
            ) : null}
        </div>
    );
}
